import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import {compose} from "mu-lambda";
import {useEffect, useState} from "react";

import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingMedium} from "@khanacademy/wonder-blocks-typography";
import {
    enums,
    usePublishAnalyticsEvent,
    withConversionContext,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {
    DateRangeDropdown,
    getSelectedDateRange,
    type DateRangeSelection,
    type DateRangeValue,
} from "@khan/component-date-range-dropdown";
import {ErrorMessageWrapper} from "@khan/component-error-message";
import {ReportError} from "@khan/component-screens/error-loading";
import {ShimmerRectangle} from "@khan/component-shimmer";
import {useQuery} from "@khan/data-gql";
import {useLocation} from "@khan/urls";

import {useUpdatePageSettings} from "~/data-access/use-update-page-settings.ts";

import type {guideTranscripts_aiGuideThreads_threads as AIGuideThreads} from "./__graphql-types__/guideTranscripts.ts";
import type {ColumnName} from "./column-names.ts";
import {ConversationsViewHeading} from "./conversation-view-heading.tsx";
import {ConversationViewPagination} from "./conversation-view-pagination.tsx";
import ConversationsView from "./conversations-view.tsx";
import {GUIDE_TRANSCRIPTS_QUERY} from "./guide-transcripts-query.ts";
import {TableViewHeading} from "./table-view-heading.tsx";
import {TableViewPagination} from "./table-view-pagination.tsx";
import TableView from "./table-view.tsx";

interface OwnProps {
    /**
     * The kaid of the user whose chat history is being viewed.
     */
    kaid: string;
    /**
     * URL path to the user's profile, needed for conversation links.
     */
    profilePath?: string;
    /**
     * The relationship between the viewer and the profile owner. Used for
     * analytics purposes.
     */
    relationship: "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER";
    /**
     * Show the new table view instead of the conversations view.
     */
    renderTableView?: boolean;
    /**
     * For the conversations view, if a conversation is flagged, render the
     * notice in the first column, under the Chat Name.
     */
    showFlaggedNotice?: boolean;
    /**
     * The columns to display in the table. Defaults to all columns.
     * The values are the keys of the ThreadType object. Order matters.
     */
    visibleColumns?: Array<ColumnName>;
    /**
     * The title to display in the table view.
     */
    tableTitle?: string;
    /**
     * Text which shows up aside from the title in the table view.
     */
    tableBannerText?: string;
    /**
     * Pass true to not show the resume thread link in the chat modal.
     */
    hideResumeThreadLink?: boolean;
}

type Props = OwnProps & WithMarkConversionProps;

const DEFAULT_DATE_RANGE_VALUE: DateRangeValue = "last-30-days";
const GUIDE_TRANSCRIPTS_QUERY_PAGE_SIZE = 10;

/**
 * Gets the correct ISO datetime strings for the given date range.
 *
 * For start dates, we modify the time to the start of the day, while for end
 * dates, we modify the time to the very end of the day before converting to
 * ISO format. Returns null values if there is not a valid date or date range
 * provided.
 */
const dateRangeToISOStrings = (
    selection: DateRangeSelection,
): {startDate: string | null; endDate: string | null} => {
    const dateRange = selection.dateRange;
    const startDate = dateRange?.startDate
        ? moment(dateRange.startDate).startOf("day").toISOString()
        : null;
    const endDate = dateRange?.endDate
        ? moment(dateRange.endDate).endOf("day").toISOString()
        : null;
    return {startDate, endDate};
};

/**
 * Renders a view of AI guide transcripts (chat history) with various filtering
 * options. NOTE: this is used in both Classic Khan and Classroom.
 *
 * @param props - The component props
 * @param props.kaid - User's Khan Academy ID
 * @param props.markConversion - Function to mark analytics conversions
 * @param props.profilePath - Path to the user profile, needed for conversation links
 * @param props.relationship - Relationship between the viewer and the profile owner
 * @param props.renderTableView - Whether to render history as a table view (vs conversation view)
 * @param props.showFlaggedNotice - Whether to show notices for flagged content
 * @param props.visibleColumns - Columns to display in the table view
 * @param props.tableTitle - Title to display in the table view
 * @param props.tableBannerText - Text to show aside from the title in the table view
 * @param props.hideResumeThreadLink - Pass true to not show the resume thread link in the chat modal
 *
 * @returns React element showing guide transcripts with pagination and filters
 */
const GuideTranscripts = ({
    kaid,
    markConversion,
    profilePath,
    relationship,
    renderTableView,
    showFlaggedNotice,
    visibleColumns,
    tableTitle,
    tableBannerText,
    hideResumeThreadLink,
}: Props): React.ReactElement => {
    useOnMountEffect(() => {
        markConversion({id: "guide_transcripts_page_view"});
    });
    useUpdatePageSettings({title: t`Khanmigo Chat History`});

    const {
        queryParams: {
            cursor,
            selectFlaggedChats,
            startDate: queryParamsStartDate,
            endDate: queryParamsEndDate,
        },
        pathname,
    } = useLocation();

    const [dateRangeSelection, setDateRangeSelection] =
        useState<DateRangeSelection>(
            getSelectedDateRange(
                null,
                renderTableView ? "*" : DEFAULT_DATE_RANGE_VALUE,
            ),
        );

    useEffect(() => {
        if (queryParamsStartDate && queryParamsEndDate) {
            setDateRangeSelection({
                selectedValue: "custom",
                dateRange: {
                    startDate: moment(queryParamsStartDate).toDate(),
                    endDate: moment(queryParamsEndDate).toDate(),
                },
            });
        }
    }, [queryParamsStartDate, queryParamsEndDate, setDateRangeSelection]);

    const {startDate, endDate} = dateRangeToISOStrings(dateRangeSelection);

    const [flaggedThreadsOnly, setFlaggedThreadsOnly] =
        useState<boolean>(!!selectFlaggedChats);

    const [result] = useQuery(GUIDE_TRANSCRIPTS_QUERY, {
        variables: {
            kaid,
            cursor,
            startDate,
            endDate,
            moderationStatus: flaggedThreadsOnly ? "FLAGGED" : "ALL",
            limit: GUIDE_TRANSCRIPTS_QUERY_PAGE_SIZE,
        },
    });

    let threads: Array<AIGuideThreads> = [];
    if (result.status === "success") {
        threads = result.data?.aiGuideThreads?.threads || [];
    }

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    useEffect(() => {
        publishAnalyticsEvent({
            eventType: "AiGuideHistoryOpened",
            eventSchemaVersion: 3,
            reviewerRelationship: relationship,
        });

        return function cleanup() {
            publishAnalyticsEvent({
                eventType: "AiGuideHistoryClosed",
                eventSchemaVersion: 3,
                reviewerRelationship: relationship,
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- NOTE(john): We only want this to run once on mount.
    }, []);

    let cursorResult: string | null | undefined = null;

    /**
     * Renders the loading and error content for the query.
     * Returns null once we're ready to render the loaded content.
     */
    const renderNonSuccessContent = (): React.ReactNode => {
        if (result.status === "loading" || result.status === "no-data") {
            return (
                <>
                    <ShimmerRectangle
                        animated={true}
                        height="33vh"
                        width="100%"
                    />
                </>
            );
        }
        if (result.status !== "success") {
            return (
                <ErrorMessageWrapper>
                    <HeadingMedium style={{textAlign: "center"}}>
                        {t`Oops. Something went wrong. Please try again.`}
                    </HeadingMedium>
                    <Body
                        style={{
                            textAlign: "center",
                            marginBlockStart: spacing.xLarge_32,
                        }}
                    >
                        <ReportError />
                    </Body>
                </ErrorMessageWrapper>
            );
        }
    };

    /**
     * Renders the loaded content based on the result of the query.
     */
    const renderLoadedContent = (): React.ReactNode => {
        if (result.status !== "success") {
            return renderNonSuccessContent();
        }

        cursorResult = result.data?.aiGuideThreads?.cursor;

        if (renderTableView) {
            return (
                <TableView
                    threads={threads}
                    showFlaggedNotice={showFlaggedNotice}
                    visibleColumns={visibleColumns}
                    hideResumeThreadLink={hideResumeThreadLink}
                    relationship={relationship}
                />
            );
        }
        if (profilePath) {
            // Classic view needs profile path for thread link
            return (
                <ConversationsView
                    threads={threads}
                    profilePath={profilePath}
                />
            );
        }
    };

    return (
        <View>
            {renderTableView ? (
                <>
                    {relationship === "COACH" ||
                    relationship === "DISTRICT_ADMIN" ? (
                        <View style={styles.filtersContainer}>
                            <DateRangeDropdown
                                onChange={setDateRangeSelection}
                                dateRangeSelection={dateRangeSelection}
                                style={styles.dateRangeDropdown}
                                maxDate={moment()}
                            />
                            <Checkbox
                                label={t`Only show flagged chats`}
                                checked={flaggedThreadsOnly}
                                onChange={setFlaggedThreadsOnly}
                            />
                        </View>
                    ) : null}
                    {threads.length ? (
                        <TableViewHeading
                            title={tableTitle}
                            subtitle={tableBannerText}
                        />
                    ) : null}
                </>
            ) : (
                <ConversationsViewHeading
                    profilePath={profilePath}
                    dateRangeSelection={dateRangeSelection}
                    setDateRangeSelection={setDateRangeSelection}
                    flaggedThreadsOnly={flaggedThreadsOnly}
                    setFlaggedThreadsOnly={setFlaggedThreadsOnly}
                />
            )}
            <Strut size={spacing.large_24} />
            {renderLoadedContent()}
            {renderTableView ? (
                <TableViewPagination
                    cursor={cursor}
                    cursorResult={cursorResult}
                    pathname={pathname}
                />
            ) : (
                <>
                    <Strut size={spacing.medium_16} />
                    <ConversationViewPagination
                        cursor={cursor}
                        cursorResult={cursorResult}
                        pathname={pathname}
                    />
                </>
            )}
            <Strut size={spacing.medium_16} />
        </View>
    );
};

const styles = StyleSheet.create({
    filtersContainer: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.large_24,
    },
    dateRangeDropdown: {
        width: "28.8rem",
    },
});

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof GuideTranscripts,
        React.ComponentProps<typeof GuideTranscripts>
    >
>;

const GuideTranscriptsWithComposed = compose(
    withConversionContext({
        activity: enums.Activity.OTHER_ACTIVITY,
    }),
    withMarkConversion,
)(GuideTranscripts) as React.ComponentType<ExportProps>;
export default GuideTranscriptsWithComposed;
